import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import { Loader2, Save } from "lucide-react";

import { GET_SETTINGS } from "@/graphql/queries/admin";
import { SAVE_SETTINGS } from "@/graphql/mutations/admin";
import { KidModeSettings } from "@/components/settings/kid-mode-settings";
import { useAuth } from "@/lib/auth/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { BetaBadge } from "@/components/ui/beta-badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

// Each kind of AI work can be pointed at either provider independently, so a
// provider can be tried on the chat before the generators are moved over.
const AI_PURPOSES = [
  {
    key: "chat" as const,
    label: "Pokalbis su mokiniu",
    hint: "Aiškinimasis po neteisingo atsakymo. Mokinys laukia, todėl Gemini čia visada naudoja mokamą raktą ir standartinį (ne „flex“) tarifą, o Claude — Opus 5.5 greitąjį režimą (apie 2 s atsakymui, dvigubai brangiau).",
  },
  {
    key: "generation" as const,
    label: "Užduočių ir teorijos generavimas",
    hint: "Vienas didelis atsakymas. Svarbiausia kokybė, ne greitis.",
  },
  {
    key: "evaluation" as const,
    label: "Užduočių vertinimas",
    hint: "Sugeneruotų užduočių kokybės tikrinimas. Vyksta fone, niekas nelaukia.",
  },
  {
    key: "adjudication" as const,
    label: "Atsakymų arbitražas",
    hint: "Ar mokinio atsakymas vis dėlto priimtinas, ar pati užduotis ydinga. Mokinys laukia vidury sesijos, todėl Gemini čia naudoja standartinį (ne „flex“) tarifą — išmatuota: 3,7 s vietoj 84,7 s.",
  },
  {
    key: "tagging" as const,
    label: "Sąvokų žymėjimas",
    hint: "Parenka sąvoką iš gatavo sąrašo — ne rašymas, o rūšiavimas. Tūkstančiai užduočių, todėl svarbiausia kaina.",
  },
  {
    key: "reports" as const,
    label: "Pažangos ataskaitos",
    hint: "Laiškas mokiniui ir ataskaita mokytojui. Vienas ilgas tekstas, kurį skaito žmogus — svarbiausia, kaip parašyta.",
  },
];

export default function SettingsPage() {
  const { isAdmin, impersonationEnabled } = useAuth();
  const [language, setLanguage] = useState("lt");
  const [aiMode, setAiMode] = useState("auto");
  const [providers, setProviders] = useState({
    chat: "gemini",
    generation: "gemini",
    evaluation: "gemini",
    adjudication: "gemini",
    tagging: "deepseek",
    reports: "deepseek",
  });
  const [deepseekThinking, setDeepseekThinking] = useState(false);
  // Off means every account has unlimited AI. See AiCredits.metered? for why
  // that is the default rather than the exception.
  const [creditsMetered, setCreditsMetered] = useState(false);
  const [autoReports, setAutoReports] = useState(true);
  const [allowKidMode, setAllowKidMode] = useState(impersonationEnabled);
  // "auto" — however many the learner did last time. A number pins it for
  // everyone. Kept as a string because that is what Select speaks.
  const [defaultCount, setDefaultCount] = useState("auto");

  // The settings record holds the API key, so only an admin may read it. A
  // guardian still gets this page — just the family half of it.
  const { data, loading } = useQuery(GET_SETTINGS, { skip: !isAdmin });
  const [saveSettings, { loading: isSaving }] = useMutation(SAVE_SETTINGS);

  useEffect(() => {
    if (data?.settings) {
      if (data.settings.geminiMode) {
        setAiMode(data.settings.geminiMode);
      }
      setProviders({
        chat: data.settings.chatAiProvider,
        generation: data.settings.generationAiProvider,
        evaluation: data.settings.evaluationAiProvider,
        adjudication: data.settings.adjudicationAiProvider,
        tagging: data.settings.taggingAiProvider,
        reports: data.settings.reportsAiProvider,
      });
      setDeepseekThinking(data.settings.deepseekChatThinking);
      setCreditsMetered(data.settings.aiCreditsMetered);
      setAutoReports(data.settings.autoGuardianReports);
      setAllowKidMode(data.settings.impersonationEnabled);
      setDefaultCount(
        data.settings.defaultExerciseCount
          ? String(data.settings.defaultExerciseCount)
          : "auto"
      );
    }
    setLanguage(localStorage.getItem("language") || "lt");
  }, [data]);

  async function handleSave() {
    try {
      await saveSettings({
        variables: {
          geminiMode: aiMode,
          chatAiProvider: providers.chat,
          generationAiProvider: providers.generation,
          evaluationAiProvider: providers.evaluation,
          adjudicationAiProvider: providers.adjudication,
          taggingAiProvider: providers.tagging,
          reportsAiProvider: providers.reports,
          deepseekChatThinking: deepseekThinking,
          aiCreditsMetered: creditsMetered,
          autoGuardianReports: autoReports,
          // Zero clears the pin — the server reads it as "as many as last time".
          defaultExerciseCount: defaultCount === "auto" ? 0 : Number(defaultCount),
        },
      });

      // Save language preference
      localStorage.setItem("language", language);

      toast.success("Nustatymai išsaugoti");
    } catch {
      toast.error("Nepavyko išsaugoti nustatymų");
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Nustatymai</h1>
        <p className="text-muted-foreground">
          Konfiguruokite programos nustatymus
        </p>
      </div>

      <div className="grid gap-6 max-w-2xl">
        <KidModeSettings
          impersonationEnabled={isAdmin ? allowKidMode : impersonationEnabled}
          onImpersonationChange={setAllowKidMode}
        />

        {/* AI provider routing — admin only. API keys are not here: they
            live in Rails credentials. */}
        {isAdmin && (
          <>
        {/* How long a session a learner starts for themselves */}
        <Card>
          <CardHeader>
            <CardTitle>Mokinio sesijos ilgis</CardTitle>
            <CardDescription>
              Kiek užduočių gauna mokinys, pradėjęs mokytis pats. Mokinys gali
              pasirinkti kitaip savo ekrane — čia nustatoma tik pradinė reikšmė.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Label htmlFor="defaultCount">Užduočių skaičius</Label>
            <Select value={defaultCount} onValueChange={setDefaultCount}>
              <SelectTrigger id="defaultCount">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Kaip praėjusį kartą</SelectItem>
                <SelectItem value="5">5 užduotys</SelectItem>
                <SelectItem value="10">10 užduočių</SelectItem>
                <SelectItem value="15">15 užduočių</SelectItem>
                <SelectItem value="20">20 užduočių</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {defaultCount === "auto"
                ? "Sesija bus tokio pat ilgumo, kaip paskutinė mokinio sesija (jei tokios nėra — 10)."
                : "Visos naujos mokinio sesijos bus tokio ilgumo, kol čia nepakeisite."}
            </p>
          </CardContent>
        </Card>

        {/* Which provider serves which kind of work */}
        <Card>
          <CardHeader>
            <CardTitle>AI teikėjai</CardTitle>
            <CardDescription>
              Pasirinkite, kuris AI atlieka kiekvieną darbą. Galima keisti po
              vieną — pvz. pokalbį perkelti į DeepSeek, o generavimą laikyti su
              Gemini.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {AI_PURPOSES.map((purpose) => (
              <div key={purpose.key} className="space-y-2">
                <Label htmlFor={`provider-${purpose.key}`}>
                  {purpose.label}
                </Label>
                <Select
                  value={providers[purpose.key]}
                  onValueChange={(value) =>
                    setProviders((prev) => ({ ...prev, [purpose.key]: value }))
                  }
                >
                  <SelectTrigger id={`provider-${purpose.key}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gemini">Google Gemini</SelectItem>
                    <SelectItem value="deepseek">DeepSeek V4</SelectItem>
                    <SelectItem value="anthropic">Anthropic Claude Opus 5.5</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">{purpose.hint}</p>
              </div>
            ))}

            {/* Thinking is worth an experiment: better judgement of a retry
                answer, but a slower reply for a child who is waiting. */}
            <div className="flex items-start gap-3 border-t pt-4">
              <Checkbox
                id="deepseekThinking"
                checked={deepseekThinking}
                onCheckedChange={(value) => setDeepseekThinking(value === true)}
              />
              <div className="space-y-1">
                <Label htmlFor="deepseekThinking" className="font-medium">
                  DeepSeek mąsto prieš atsakydamas (pokalbyje)
                </Label>
                <p className="text-sm text-muted-foreground">
                  Išjungta: atsakymas ~35% greitesnis ir maždaug keturis kartus
                  mažiau tokenų. Įjunkite, jei norite palyginti atsakymų kokybę.
                  Veikia tik tada, kai pokalbį aptarnauja DeepSeek — pagal
                  nutylėjimą tai daro Gemini.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Whether AI use is charged at all. Off since the allowance shipped
            with every learner at zero, which refused the chat and the avatar
            generator for everyone but the admin. */}
        <Card>
          <CardHeader>
            <CardTitle>AI kreditai</CardTitle>
            <CardDescription>
              Ar AI funkcijos — pokalbis su mokiniu, „Noriu suprasti“, avataro
              generavimas, pritaikymas ir ataskaitos — nurašomos nuo paskyros
              kreditų.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-3">
              <Checkbox
                id="creditsMetered"
                checked={creditsMetered}
                onCheckedChange={(value) => setCreditsMetered(value === true)}
              />
              <div className="space-y-1">
                <Label htmlFor="creditsMetered" className="font-medium">
                  Skaičiuoti AI kreditus
                </Label>
                <p className="text-sm text-muted-foreground">
                  {creditsMetered
                    ? "Kiekvienas AI veiksmas nurašomas nuo prenumeratos kreditų. Pasibaigus kreditams, mokinys AI pagalbos nebegaus, kol paskyra nebus papildyta."
                    : "Išjungta: AI neribojamas nė vienai paskyrai. Kreditų likučiai ir istorija saugomi, bet niekas jų neskaičiuoja."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Whether finishing a session writes the grown-up's report by itself.
            On by default: a report you have to remember to ask for is one you
            read after you already noticed the problem. */}
        <Card>
          <CardHeader>
            <CardTitle>Ataskaitos po sesijų</CardTitle>
            <CardDescription>
              Ar baigta sesija pati parengia tėvams skirtą ataskaitą — su tuo, kur
              vaikas stringa, ir su mygtukais, ką dėl to pakeisti.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-3">
              <Checkbox
                id="autoReports"
                checked={autoReports}
                onCheckedChange={(value) => setAutoReports(value === true)}
              />
              <div className="space-y-1">
                <Label htmlFor="autoReports" className="font-medium">
                  Rengti ataskaitą po kiekvienos sesijos
                </Label>
                <p className="text-sm text-muted-foreground">
                  {autoReports
                    ? "Ataskaita parašoma fone, iškart po sesijos — vaikas jos nelaukia ir nemato. Rasite ją Mokinys → Ataskaitos."
                    : "Išjungta: ataskaita parengiama tik paspaudus „Parengti“ mokinio ataskaitų puslapyje."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI mode (free / paid tier) */}
        <Card>
          <CardHeader>
            <CardTitle>Gemini raktų režimas</CardTitle>
            <CardDescription>
              Taikoma tik Gemini darbams: pasirinkite, kurį raktą naudoti.
              Mokamas režimas naudoja pigesnį „flex“ tarifą. Pokalbis su mokiniu
              čia neįeina — jis visada naudoja mokamą raktą ir standartinį
              tarifą, nes mokinys laukia atsakymo.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="aiMode">Režimas</Label>
              <Select value={aiMode} onValueChange={setAiMode}>
                <SelectTrigger id="aiMode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">
                    Automatinis (nemokamas, po to mokamas)
                  </SelectItem>
                  <SelectItem value="free">Tik nemokamas</SelectItem>
                  <SelectItem value="paid">Tik mokamas (flex)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {aiMode === "auto" &&
                  "Pirmiausia bandomas nemokamas raktas; jei jis nepasiekiamas, automatiškai naudojamas mokamas (flex) raktas."}
                {aiMode === "free" &&
                  "Naudojamas tik nemokamas raktas. Pasiekus limitą, generavimas nepavyks."}
                {aiMode === "paid" &&
                  "Visada naudojamas mokamas raktas su pigesniu „flex“ tarifu."}
              </p>
            </div>
          </CardContent>
        </Card>
          </>
        )}

        {/* Language */}
        <Card>
          <CardHeader>
            <CardTitle>Kalba</CardTitle>
            <CardDescription>Pasirinkite programos kalbą</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="language">Kalba</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger id="language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lt">Lietuvių</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* About */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Apie programą
              <BetaBadge />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              <strong>Scio</strong> — mokinių įgūdžių sekimo ir užduočių sistema.
            </p>
            <p>
              Tai <strong>beta versija</strong>: veikianti ir naudojama kasdien, bet dar
              nebaigta. Dalis ekranų ir užduočių dar tvarkoma, todėl kur nors pasitaikys
              nesutvarkytas kampas. Vaiko atsakymai ir pažanga dėl to nenukenčia.
            </p>
            <p>
              Radus sugedusią užduotį, ją galima pažymėti pačioje užduotyje („…" →
              „Kažkas negerai su šia užduotimi") — pranešimas patenka į pažymėto turinio
              sąrašą.
            </p>
            <p>
              Įgūdžiai konfigūruojami faile{" "}
              <code className="bg-muted px-1 rounded">config/skills.yml</code> ir
              automatiškai sinchronizuojami į duomenų bazę.
            </p>
          </CardContent>
        </Card>

        {/* Save Button — writes the Gemini settings above. */}
        {isAdmin && (
          <Button onClick={handleSave} disabled={isSaving} className="w-fit">
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="mr-2 h-4 w-4" />
            Išsaugoti nustatymus
          </Button>
        )}
      </div>
    </div>
  );
}
